/* eslint-disable no-param-reassign */
import {
	BroadcastInsights,
	BroadcastReplyType,
	BroadcastWithClientData,
} from 'types'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import getBroadcast, {
	GetBroadcastBody,
	GetBroadcastResponse,
} from 'api/broadcasts/getBroadcast'
import handleAxiosError from 'utils/handleAxiosError'
import GetBroadcastReplies, {
	GetBroadcastRepliesParams,
	GetBroadcastRepliesResponse,
} from 'api/broadcast/getBroadcastReplies'

const BROADCAST_REPLIES_FETCH_SIZE = 10

interface Pagination {
	currentPage: number
	size: number
	totalCount: number
	totalPages: number
}

interface BroadcastState {
	broadcast: BroadcastWithClientData | null
	replies: Array<BroadcastReplyType>
	repliesLoading: boolean
	repliesPagination: Pagination
	loading: boolean
	insights: BroadcastInsights | null
}

export const getBroadcastAction = createAsyncThunk<
	GetBroadcastResponse,
	GetBroadcastBody
>('broadcast/getBroadcastAction', async (payload, { rejectWithValue }) => {
	try {
		return await getBroadcast(payload)
	} catch (err: any) {
		handleAxiosError(err)
		return rejectWithValue('Failed to fetch broadcast')
	}
})

export const getBroadcastRepliesAction = createAsyncThunk<
	GetBroadcastRepliesResponse,
	GetBroadcastRepliesParams
>('broadcast/getBroadcastReplies', async (payload, { rejectWithValue }) => {
	try {
		return await GetBroadcastReplies(payload)
	} catch (err: any) {
		handleAxiosError(err)
		return rejectWithValue('Failed to fetch broadcast replies')
	}
})

// export const getBroadcastInsightsAction = createAsyncThunk<BroadcastInsights>()

const initialState: BroadcastState = {
	broadcast: null,
	loading: false,
	insights: null,
	replies: [],
	repliesLoading: false,
	repliesPagination: {
		currentPage: 1,
		size: BROADCAST_REPLIES_FETCH_SIZE,
		totalCount: 0,
		totalPages: 0,
	},
}

const broadcastSlice = createSlice({
	name: 'broadcast',
	initialState,
	reducers: {
		clearBroadcast: () => {
			return initialState
		},
	},
	extraReducers: builder => {
		builder.addCase(getBroadcastAction.pending, state => {
			state.loading = true
			state.broadcast = null
			state.insights = null
		})
		builder.addCase(getBroadcastAction.fulfilled, (state, { payload }) => {
			state.loading = false
			state.broadcast = payload.broadcast
			state.insights = payload.insights
		})
		builder.addCase(getBroadcastAction.rejected, state => {
			state.loading = false
		})
		/**
		 * Replies
		 */
		builder.addCase(getBroadcastRepliesAction.pending, state => {
			state.repliesLoading = true
		})
		builder.addCase(getBroadcastRepliesAction.fulfilled, (state, { payload }) => {
			state.repliesLoading = false

			const response = { ...payload.response }

			state.replies = response.records
			state.repliesPagination = {
				...state.repliesPagination,
				currentPage: response.currentPage,
				totalCount: response.totalCount,
				totalPages: response.totalPages,
			}
		})
		builder.addCase(getBroadcastRepliesAction.rejected, state => {
			state.repliesLoading = false
		})
	},
})

export const { clearBroadcast } = broadcastSlice.actions

export default broadcastSlice.reducer
